import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useCurrency } from '../hooks/useCurrency';
import { cleanName } from '../lib/format';
import type { KitSimulado, SkinGroup } from '../api/types';
import { BotaoKitNaSacola } from './BotaoKitNaSacola';
import { itemDoGrupo, reais, trackKitBuild } from '../lib/tracking';

/** Uma peça escolhida pelo cliente, com o desgaste que ele fixou. */
interface Escolha {
  baseKey: string;
  name: string;
  iconUrl: string | null;
  variantes: SkinGroup['variants'];
  exterior: string;
}

const daGrupo = (g: SkinGroup): Escolha => ({
  baseKey: g.key,
  name: g.name,
  iconUrl: g.iconUrl,
  variantes: g.variants ?? [],
  exterior: (g.variants ?? []).find((v) => v.price === g.minPrice)?.exterior?.code ?? '',
});

/**
 * Busca de uma peça, restrita a faca ou luva.
 *
 * O `kind` vai para a API: o cliente que digita "bayonet" no campo da luva não
 * recebe facas, e não consegue montar um par inválido sem perceber.
 */
function Escolher({
  kind,
  rotulo,
  exemplo,
  escolhida,
  onEscolher,
}: {
  kind: 'knife' | 'gloves';
  rotulo: string;
  exemplo: string;
  escolhida: Escolha | null;
  onEscolher: (e: Escolha | null) => void;
}) {
  const { money } = useCurrency();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<SkinGroup[] | null>(null);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 3) {
      setResultados(null);
      return;
    }
    setBuscando(true);
    const t = window.setTimeout(() => {
      void api
        .skins({ kind, search: termo, pageSize: 12, sort: 'onsale' })
        .then((r) => setResultados(r.items))
        .catch(() => setResultados([]))
        .finally(() => setBuscando(false));
    }, 400);
    return () => window.clearTimeout(t);
  }, [busca, kind]);

  if (escolhida) {
    return (
      <div className="kb-slot escolhido">
        <span className="kb-rotulo">{rotulo}</span>
        {escolhida.iconUrl ? <img src={escolhida.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
        <b>{cleanName(escolhida.name)}</b>
        {/* Fixar o desgaste é o que faz o preço ser o da peça que o cliente
            vai receber, e não o da variante mais barata do momento. */}
        <select
          className="select"
          value={escolhida.exterior}
          onChange={(e) => onEscolher({ ...escolhida, exterior: e.target.value })}
        >
          {escolhida.variantes
            .filter((v) => v.exterior && v.price !== null)
            .map((v) => (
              <option key={v.exterior!.code} value={v.exterior!.code}>
                {v.exterior!.pt} · {money(v.price)}
              </option>
            ))}
        </select>
        <button className="btn ghost small" onClick={() => onEscolher(null)}>
          Trocar
        </button>
      </div>
    );
  }

  return (
    <div className="kb-slot">
      <span className="kb-rotulo">{rotulo}</span>
      <input className="input" placeholder={exemplo} value={busca} onChange={(e) => setBusca(e.target.value)} />
      {buscando && <span className="dim">buscando…</span>}
      {resultados && resultados.length === 0 && !buscando && <span className="dim">nada encontrado</span>}
      {resultados && resultados.length > 0 && (
        <div className="kb-resultados">
          {resultados.map((g) => (
            <button key={g.key} className="kb-opcao" onClick={() => onEscolher(daGrupo(g))}>
              {g.iconUrl ? <img src={g.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
              <span className="kb-opcao-nome">{cleanName(g.name)}</span>
              <span className="dim">{money(g.minPrice)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * O cliente monta o kit dele sem falar com ninguém.
 *
 * Os kits do painel são sugestão de combinação, não a lista do que pode virar
 * kit: quem já sabe qual faca quer teria de pedir pelo atendimento só para
 * receber o mesmo desconto. A conta vem da API porque a cotação de kit mora
 * no servidor — o navegador não tem como calcular, nem deveria.
 */
export function KitBuilder({ maiorDesconto }: { maiorDesconto: number }) {
  const { money } = useCurrency();
  const [faca, setFaca] = useState<Escolha | null>(null);
  const [luva, setLuva] = useState<Escolha | null>(null);
  const [conta, setConta] = useState<KitSimulado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [calculando, setCalculando] = useState(false);
  const pctTeto = Math.round(maiorDesconto);
  /** Último par contado: trocar de desgaste e voltar não conta outra montagem. */
  const ultimoPar = useRef('');

  useEffect(() => {
    if (!faca || !luva) {
      setConta(null);
      setErro(null);
      return;
    }
    setCalculando(true);
    setErro(null);
    void api
      .simularKit({
        knife: faca.baseKey,
        knifeExterior: faca.exterior || undefined,
        gloves: luva.baseKey,
        glovesExterior: luva.exterior || undefined,
      })
      .then((c) => {
        setConta(c);
        const par = [faca.baseKey, faca.exterior, luva.baseKey, luva.exterior].join('|');
        if (par !== ultimoPar.current) {
          ultimoPar.current = par;
          trackKitBuild({
            items: [itemDoGrupo(c.knife), itemDoGrupo(c.gloves)],
            valorBrl: reais(c.precoCny),
            descontoPct: c.descontoPct,
          });
        }
      })
      .catch((e) => {
        setConta(null);
        setErro(e instanceof ApiError ? e.message : 'Não consegui calcular este kit.');
      })
      .finally(() => setCalculando(false));
  }, [faca, luva]);

  return (
    <article className="kit-card kit-card-convite">
      <h2>
        Monte o seu kit
        {pctTeto > 0 && (
          <>
            {' '}
            e ganhe até <span className="kit-convite-pct">{pctTeto}%</span> de desconto
          </>
        )}
      </h2>
      <p>
        Escolha a faca e a luva que você quiser. O desconto de kit vale do mesmo jeito — os pares ao lado são só
        sugestão de combinação.
      </p>

      <div className="kb-slots">
        <Escolher kind="knife" rotulo="Faca" exemplo="Karambit, Butterfly, M9…" escolhida={faca} onEscolher={setFaca} />
        <Escolher
          kind="gloves"
          rotulo="Luva"
          exemplo="Sport, Specialist, Driver…"
          escolhida={luva}
          onEscolher={setLuva}
        />
      </div>

      {/* A conta só aparece com as duas escolhidas: meio kit não tem preço. */}
      {calculando && <span className="dim">calculando…</span>}
      {erro && <span className="kb-erro">{erro}</span>}
      {conta && !calculando && (
        <div className="kit-card-conta">
          <span className="kit-soma">{money(conta.somaCny)}</span>
          <b className="kit-preco">{money(conta.precoCny)}</b>
          {conta.economiaCny > 0 ? (
            <span className="kit-economia">
              economize {money(conta.economiaCny)} ({conta.descontoPct.toFixed(1)}%)
            </span>
          ) : (
            <span className="dim">estas duas peças não têm desconto de kit hoje</span>
          )}
        </div>
      )}

      {/* O par vai com o desgaste escolhido; sem escolha, o mais barato à venda. */}
      {faca && luva && conta && !calculando && (
        <BotaoKitNaSacola
          knife={{ grupo: conta.knife, exterior: faca.exterior || null }}
          gloves={{ grupo: conta.gloves, exterior: luva.exterior || null }}
        />
      )}
    </article>
  );
}
